import { message } from 'antd';
import get from 'lodash/get';
import { call, put, select, takeEvery, takeLatest } from 'redux-saga/effects';

import { createFilter, getFilters, updateFilter } from '@api/filters';
import { categoryMap } from '@components/filter-panel/constants';
import * as commonTypes from '@store/common/types';

import { clearFilterCache } from './actions';
import { isCacheValidSelector } from './selectors';
import * as types from './types';

function handleErrorResp(err) {
  const { response } = err;
  if (response) {
    const { data, statusText, status } = response;
    if (status === 403) {
      if (data.fv) {
        const { fv } = data;
        message.warning(`Filter with same rule found: ${fv.name}`);
      }
    } else if (data) {
      message.warning(Object.values(data)[0].join(''));
    } else if (statusText) {
      message.warning(statusText);
    }
  }
}

export function* create(action) {
  try {
    yield put({ type: types.START_SAVING_CHANGES });
    const resp = yield call(createFilter, action.payload);
    const { name } = resp.data;
    yield put({ type: types.GET_FILTERS });
    message.info(`Filter ${name} created`);
  } catch (err) {
    handleErrorResp(err);
  } finally {
    yield put({ type: types.DONE_SAVING_CHANGES });
  }
}

export function* apply(action) {
  try {
    yield put({
      type: commonTypes.APPLY_FILTER,
      filter: action.payload,
    });
  } catch (err) {
    handleErrorResp(err);
  } finally {
    yield put({
      type: types.CHANGE_FILTER_CONTAINER_VISIBILITY,
      visible: false,
    });
  }
}

export function* createAndApply(action) {
  try {
    yield put({ type: types.START_SAVING_CHANGES });
    const resp = yield call(createFilter, action.payload);
    const { name, id } = resp.data;
    message.info(`Filter ${name} created`);
    const { rule } = action.payload;
    yield put({
      type: types.CHANGE_FILTER_CONTAINER_VISIBILITY,
      visible: false,
    });
    yield put({
      type: commonTypes.APPLY_FILTER,
      filter: {
        name,
        rule,
        fid: id,
      },
    });
  } catch (err) {
    handleErrorResp(err);
  } finally {
    yield put({ type: types.DONE_SAVING_CHANGES });
  }
}

export function* update(action) {
  try {
    yield put({ type: types.START_SAVING_CHANGES });
    const resp = yield call(updateFilter, action.fid, action.payload);
    const { name } = resp.data;
    yield put({ type: types.GET_FILTERS });
    message.info(`Filter ${name} updated`);
  } catch (err) {
    handleErrorResp(err);
  } finally {
    yield put({ type: types.DONE_SAVING_CHANGES });
  }
}

export function* updateAndApply(action) {
  try {
    yield put({ type: types.START_SAVING_CHANGES });
    const resp = yield call(updateFilter, action.fid, action.payload);
    const { name } = resp.data;
    message.info(`Filter ${name} updated`);
    const { rule } = action.payload;
    yield put({
      type: types.CHANGE_FILTER_CONTAINER_VISIBILITY,
      visible: false,
    });
    yield put({
      type: commonTypes.APPLY_FILTER,
      filter: {
        name,
        rule,
        fid: action.fid,
      },
    });
  } catch (err) {
    handleErrorResp(err);
  } finally {
    yield put({ type: types.DONE_SAVING_CHANGES });
  }
}

export function* getAllFilters() {
  try {
    // check for cached filter list
    const isCacheValid = yield select(isCacheValidSelector);
    if (isCacheValid) {
      return;
    }

    const resp = yield call(getFilters);
    yield put({ type: types.DONE_GETTING_FILTERS, filters: resp });
  } catch (err) {
    const { response } = err;
    if (response) {
      // must ensure the messages exist,
      // or the null value will occur an error and catch all the actions of rootsaga
      const { data = {} } = response;
      if (typeof data === 'string') {
        message.warning(data);
      } else {
        const messages = get(Object.values(data), [0], []);
        message.warning(messages.join(''));
      }
    }
  }
}

function* applyFilterSaga() {
  yield takeLatest(types.APPLY_FILTER, apply);
}

function* createFilterSaga() {
  yield takeEvery(types.CREATE_FILTER, create);
}

function* createAndApplyFilterSaga() {
  yield takeEvery(types.CREATE_AND_APPLY, createAndApply);
}

function* updateFilterFilterSaga() {
  yield takeEvery(types.UPDATE_FILTER, update);
}

function* updateAndApplyFilterFilterSaga() {
  yield takeEvery(types.UPDATE_AND_APPLY, updateAndApply);
}

// clear filter cache on any action which updates filters
function* clearFilterCacheSaga() {
  yield takeEvery(
    [types.CREATE_FILTER, types.CREATE_AND_APPLY, types.UPDATE_FILTER, types.UPDATE_AND_APPLY],
    function* clearCache() {
      yield put(clearFilterCache());
    },
  );
}

function* getFiltersSaga() {
  yield takeLatest(types.GET_FILTERS, getAllFilters);
}

function* updateCategoryAndSubcategory() {
  yield takeEvery(
    [
      types.ADD_AND_RULE,
      types.ADD_OR_RULE,
      types.UPDATE_OR_RULE,
      types.DELETE_OR_RULE,
      types.CHANGE_FILTER_CONTAINER_VISIBILITY,
    ],
    function* listen() {
      const state = yield select();
      const { visible } = state.filterContainer;
      if (visible) {
        const { rule } = state.filterContainer.newFilterPanel;
        const { field } = rule[0][0];
        const option = categoryMap.get(field);
        if (option) {
          yield put({
            type: types.UPDATE_FILTER_CONDITIONS,
            payload: { ...option },
          });
        }
      }
    },
  );
}

const filterContainerSagas = [
  clearFilterCacheSaga,
  applyFilterSaga,
  createFilterSaga,
  createAndApplyFilterSaga,
  updateFilterFilterSaga,
  updateAndApplyFilterFilterSaga,
  getFiltersSaga,
  updateCategoryAndSubcategory,
];

export default filterContainerSagas;
